// SignalRContext.tsx
import React, { createContext, useEffect, useState } from 'react';
import { HubConnection, HubConnectionBuilder } from '@microsoft/signalr';
import useAuth from 'hooks/useAuth';
import { applyServerContext } from 'utils/axios';
import { useSnackbar } from 'notistack';
import { dispatch } from 'store';
import { setChatUsers, setChatUsersRequested, setChatUsersSavedMessages } from 'store/slices/chat';
import { getDataOnLoad } from 'services/dataLoader';

interface SignalRContextType {
  connection: HubConnection | null;
}

const SignalRContext = createContext<SignalRContextType>({ connection: null });

//* שלבי ה-reconnect (מ"ש). אחרי השלב האחרון חוזרים על 60ש' — כלומר לא מוותרים לעולם.
const RECONNECT_STEPS = [0, 5000, 10000, 20000, 60000];
//* jitter של ±20% כדי למנוע "עדר" (thundering herd) של חיבורים מסונכרנים
//* כשהשרת קם אחרי recycle/deploy וכל הלקוחות מתחברים בבת אחת.
const reconnectDelay = (retryCount: number): number => {
  const base = RECONNECT_STEPS[Math.min(retryCount, RECONNECT_STEPS.length - 1)];
  const jitter = base * 0.2 * (Math.random() * 2 - 1);
  return Math.max(0, Math.round(base + jitter));
};

const SignalRProvider: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const { token } = useAuth();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const [connection, setConnection] = useState<HubConnection | null>(null);

  const isConnectionRef = React.useRef<boolean>(false);
  const connectionRef = React.useRef<HubConnection | null>(null);
  const hadConnectedRef = React.useRef<boolean>(false);
  //* פונקציית הרמה, נגישה גם ל-handler של אירוע 'online'
  const startRef = React.useRef<() => void>(() => {});

  //* Socket connection — reconnect אינסופי + רשת ביטחון ב-onclose
  useEffect(() => {
    if (!token) return;

    let restartTimer: ReturnType<typeof setTimeout> | null = null;
    let attempts = 0;
    let starting = false;
    let stopped = false; //* ניתוק יזום (החלפת token / logout / unmount) — לא מרימים מחדש

    const resyncData = () => {
      //* מנקה את מצב הצ'אט הנוכחי וטוען מחדש את נתוני האפליקציה
      dispatch(
        setChatUsers({
          myChatUsers: [],
          notMyChatUsers: [],
          unassignedChatUsers: [],
          botsChatUsers: [],
          allChatUsers: [],
        }),
      );
      setChatUsersRequested({
        myChatUsers: false,
        notMyChatUsers: false,
        unassignedChatUsers: false,
        botsChatUsers: false,
        allChatUsers: false,
        customChatUsers: false,
      });
      dispatch(setChatUsersSavedMessages([]));

      getDataOnLoad();
    };

    const scheduleRestart = () => {
      if (restartTimer || starting || stopped || connectionRef.current) return;
      const delay = reconnectDelay(Math.min(attempts, RECONNECT_STEPS.length - 1));
      attempts += 1;
      console.log(`SignalR scheduling restart in ${delay}ms (attempt ${attempts})`);
      restartTimer = setTimeout(() => {
        restartTimer = null;
        if (!stopped) buildAndStart();
      }, delay);
    };

    async function buildAndStart() {
      if (starting || stopped || connectionRef.current) return;
      starting = true;
      const isRebuild = hadConnectedRef.current;

      const newConnection = new HubConnectionBuilder()
        .withUrl(
          `${applyServerContext(import.meta.env.VITE_APP_SOCKET_API_URL)}/hub/signalr?access_token=${token}`,
        )
        //* מדיניות reconnect שלא מוותרת לעולם, עם jitter
        .withAutomaticReconnect({
          nextRetryDelayInMilliseconds: (ctx) => reconnectDelay(ctx.previousRetryCount),
        })
        .build();

      newConnection.onreconnecting((err) => console.warn('SignalR reconnecting', err));
      newConnection.onreconnected(() => {
        console.log('SignalR reconnected');
        attempts = 0;
        resyncData();
      });
      newConnection.onclose((err) => {
        console.log('SignalR closed', err);
        connectionRef.current = null;
        isConnectionRef.current = false;
        setConnection(null);
        //* רשת ביטחון: ה-auto-reconnect ויתר / השרת סגר — ממשיכים לנסות, אלא אם יזום
        if (!stopped) scheduleRestart();
      });

      try {
        await newConnection.start();
        if (stopped) {
          //* ה-effect נוקה בזמן ה-start — לסגור את החיבור המיותר
          newConnection.stop().catch(() => {});
          return;
        }
        connectionRef.current = newConnection;
        isConnectionRef.current = true;
        setConnection(newConnection);
        attempts = 0;
        console.log('SignalR connected');
        //* אם זו הרמה-מחדש (Path B — אחרי onclose) — לסנכרן נתונים
        if (isRebuild) resyncData();
        hadConnectedRef.current = true;
      } catch (err) {
        console.error('Error while connecting SignalR: ', err);
        connectionRef.current = null;
        if (!stopped) scheduleRestart();
      } finally {
        starting = false;
      }
    }

    startRef.current = () => {
      if (!connectionRef.current) buildAndStart();
    };
    buildAndStart();

    return () => {
      stopped = true;
      startRef.current = () => {};
      if (restartTimer) {
        clearTimeout(restartTimer);
        restartTimer = null;
      }
      const conn = connectionRef.current;
      connectionRef.current = null;
      isConnectionRef.current = false;
      setConnection(null);
      if (conn) {
        conn
          .stop()
          .then(() => console.log('Connection stopped'))
          .catch((err) => console.error('Error while stopping connection: ', err));
      }
    };
  }, [token]);

  //* Online / offline indication + ניסיון הרמה מיידי כשהרשת חוזרת
  useEffect(() => {
    let offlineNotif: any = null;
    const handleOnline = () => {
      if (offlineNotif !== null) {
        closeSnackbar(offlineNotif);
        offlineNotif = null;
      }
      //* הרשת חזרה — אם הסוקט למטה, לנסות להרים מיד בלי לחכות ל-backoff
      if (!connectionRef.current) startRef.current();
    };
    const handleOffline = () => {
      offlineNotif = enqueueSnackbar('Connection lost', {
        autoHideDuration: null,
        action: () => {
          return <></>;
        },
        variant: 'warning',
      });
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [closeSnackbar, enqueueSnackbar]);

  return <SignalRContext.Provider value={{ connection }}>{children}</SignalRContext.Provider>;
};

export { SignalRContext, SignalRProvider };
